// Real analytics service that connects to Laravel backend API
// Replaces mockAnalyticsService for production use

export interface DateRange {
  start: Date;
  end: Date;
}

export interface AnalyticsMetric {
  name: string;
  value: number;
  trend: 'up' | 'down' | 'stable';
  change: number;
  period: string;
}

export interface ProjectData {
  id: string;
  name: string;
  client: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  progress: number;
  budget: number;
  spent: number;
  startDate: Date;
  endDate: Date;
  teamSize: number;
}

export interface TeamMemberData {
  id: string;
  name: string;
  role: string;
  department: string;
  productivityScore: number;
  utilization: number;
  tasksCompleted: number;
  billableHours: number;
}

export interface FinancialData {
  revenue: number;
  expenses: number;
  profit: number;
  cashFlow: number;
  period: string;
}

class AnalyticsService {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    // Use environment variable or default to localhost for Docker
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  }

  // Set authentication token
  setAuthToken(token: string) {
    this.authToken = token;
  }

  // Get authentication headers
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  // Generic API call method
  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'API call returned error');
    }

    return data.data;
  }

  // Convert date range to API format
  private formatDateRange(dateRange: DateRange): string {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    
    if (days <= 1) return 'today';
    if (days <= 7) return 'week';
    if (days <= 31) return 'month';
    if (days <= 90) return 'quarter';
    return 'year';
  }

  // Performance Analytics API calls
  async getPerformanceData(dateRange: DateRange): Promise<any> {
    const range = this.formatDateRange(dateRange);
    return this.apiCall(`/admin/analytics/performance?dateRange=${range}`);
  }

  // Project Reports API calls
  async getProjectReports(filters: any = {}): Promise<any> {
    // Handle both old (dateRange) and new (filters with dateRange) parameter formats
    let params: Record<string, string> = {};
    
    if (filters.dateRange) {
      // New format with filters object
      if (typeof filters.dateRange === 'string') {
        params.dateRange = filters.dateRange;
      } else if (filters.dateRange.start && filters.dateRange.end) {
        const range = this.formatDateRange({
          start: new Date(filters.dateRange.start),
          end: new Date(filters.dateRange.end)
        });
        params.dateRange = range;
      }
      
      // Add other filters
      if (filters.status) params.status = filters.status;
      if (filters.client) params.client = filters.client;
      if (filters.search) params.search = filters.search;
    } else if (filters.start && filters.end) {
      // Old format with direct dateRange object
      const range = this.formatDateRange(filters);
      params.dateRange = range;
    }
    
    const queryParams = new URLSearchParams(params);
    return this.apiCall(`/admin/analytics/projects?${queryParams}`);
  }

  async exportProjectReports(format: string = 'csv', reportType: string = 'projects'): Promise<any> {
    return this.apiCall('/admin/analytics/projects/export', {
      method: 'POST',
      body: JSON.stringify({ format, reportType })
    });
  }

  // Team Productivity API calls
  async getTeamProductivity(filters: any = {}): Promise<any> {
    // Handle both old (dateRange) and new (filters with dateRange) parameter formats
    let params: Record<string, string> = {};
    
    if (filters.dateRange) {
      // New format with filters object
      if (typeof filters.dateRange === 'string') {
        params.dateRange = filters.dateRange;
      } else if (filters.dateRange.start && filters.dateRange.end) {
        const range = this.formatDateRange({
          start: new Date(filters.dateRange.start),
          end: new Date(filters.dateRange.end)
        });
        params.dateRange = range;
      }
      
      // Add other filters
      if (filters.department) params.department = filters.department;
      if (filters.role) params.role = filters.role;
    } else if (filters.start && filters.end) {
      // Old format with direct dateRange object
      const range = this.formatDateRange(filters);
      params.dateRange = range;
    }
    
    const queryParams = new URLSearchParams(params);
    return this.apiCall(`/admin/analytics/team?${queryParams}`);
  }

  async exportTeamReports(format: string = 'csv', reportType: string = 'team_performance'): Promise<any> {
    return this.apiCall('/admin/analytics/team/export', {
      method: 'POST',
      body: JSON.stringify({ format, reportType })
    });
  }

  // Financial Reports API calls
  async getFinancialReports(dateRange: DateRange, currency: string = 'USD'): Promise<any> {
    const range = this.formatDateRange(dateRange);
    const params = new URLSearchParams({
      dateRange: range,
      currency
    });
    
    return this.apiCall(`/admin/analytics/financial?${params}`);
  }

  async getAccountsReceivable(): Promise<any> {
    return this.apiCall('/admin/analytics/financial/accounts-receivable');
  }

  async exportFinancialReports(format: string = 'pdf', reportType: string = 'profit_loss'): Promise<any> {
    return this.apiCall('/admin/analytics/financial/export', {
      method: 'POST',
      body: JSON.stringify({ format, reportType })
    });
  }

  // Legacy methods for backward compatibility with existing components
  async getMetrics(dateRange: DateRange): Promise<AnalyticsMetric[]> {
    try {
      const data = await this.getPerformanceData(dateRange);
      
      // Transform backend data to match expected interface
      const metrics: AnalyticsMetric[] = [];
      
      if (data.kpi_metrics) {
        data.kpi_metrics.forEach((metric: any) => {
          metrics.push({
            name: metric.title,
            value: metric.value,
            trend: metric.trend,
            change: parseFloat(metric.trend.replace(/[+%]/g, '')),
            period: this.formatDateRange(dateRange)
          });
        });
      }
      
      return metrics;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      // Fallback to empty array if API fails
      return [];
    }
  }

  async getProjects(): Promise<ProjectData[]> {
    try {
      const data = await this.getProjectReports({ start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() });
      
      // Transform backend data to match expected interface
      if (data.projects) {
        return data.projects.map((project: any) => ({
          id: project.id.toString(),
          name: project.name,
          client: project.client,
          status: project.status.toLowerCase().replace(' ', '-'),
          progress: project.progress,
          budget: project.budget,
          spent: project.spent,
          startDate: new Date(project.startDate),
          endDate: new Date(project.endDate),
          teamSize: project.team?.length || 0
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
  }

  async getTeamMembers(): Promise<TeamMemberData[]> {
    try {
      const data = await this.getTeamProductivity({ start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() });
      
      // Transform backend data to match expected interface
      if (data.team_members) {
        return data.team_members.map((member: any) => ({
          id: member.id.toString(),
          name: member.name,
          role: member.role,
          department: member.department,
          productivityScore: member.productivity_score,
          utilization: member.utilization,
          tasksCompleted: member.tasks_completed,
          billableHours: member.hours_this_week * 4 // Convert weekly to monthly estimate
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching team members:', error);
      return [];
    }
  }

  async getFinancialData(): Promise<FinancialData[]> {
    try {
      const data = await this.getFinancialReports({ start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), end: new Date() });
      
      // Transform backend data to match expected interface
      if (data.revenue_analysis?.monthly_revenue) {
        const labels = data.revenue_analysis.monthly_revenue.labels;
        const revenues = data.revenue_analysis.monthly_revenue.data;
        const expenses = data.expense_analysis?.monthly_expenses?.data || [];
        
        return labels.map((label: string, index: number) => ({
          revenue: revenues[index] || 0,
          expenses: expenses[index] || 0,
          profit: (revenues[index] || 0) - (expenses[index] || 0),
          cashFlow: ((revenues[index] || 0) - (expenses[index] || 0)) * 0.8,
          period: label
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching financial data:', error);
      return [];
    }
  }

  // Utility functions
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  // Health check for API connectivity
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

export default analyticsService;